import { useState, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileSearch, Plus, Trophy, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

type Proveedor = { id: string; rfc: string; razon_social: string };

export function CotizacionDetallePage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [openCot, setOpenCot] = useState(false);

  const { data: s } = useQuery<any>({ queryKey: ['cot-solicitud', id], queryFn: () => api(`/cotizaciones/solicitudes/${id}`) });

  async function adjudicar(cotID: string) {
    if (!confirm('¿Adjudicar esta cotización como ganadora?')) return;
    try {
      await api(`/cotizaciones/solicitudes/${id}/adjudicar`, {
        method: 'POST',
        body: { cotizacion_id: cotID },
      });
      qc.invalidateQueries({ queryKey: ['cot-solicitud', id] });
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Error');
    }
  }

  if (!s) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/app/cotizaciones" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver a cotizaciones
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <FileSearch size={20} />
            </div>
            <div>
              <p className="num text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-700">{s.folio}</p>
              <h1 className="font-display text-[24px] font-bold tracking-tight text-ink">Solicitud de cotización</h1>
              <p className="text-[12px] text-ink-mute">{fmt.date(s.fecha)} · límite {fmt.date(s.fecha_limite)} · {s.estado}</p>
            </div>
          </div>
          {s.estado === 'abierta' && (
            <Button onClick={() => setOpenCot(true)}><Plus size={14} /> Registrar cotización</Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader title="Descripción" />
        <CardBody>
          <p className="text-[13px]">{s.descripcion}</p>
          {s.requisicion_id && (
            <Link to={`/app/requisiciones/${s.requisicion_id}`} className="mt-2 inline-block text-[12px] text-brand-600">
              Ligada a requisición →
            </Link>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Matriz comparativa" subtitle={`${(s.cotizaciones ?? []).length} cotizaciones · criterio ${s.criterio_evaluacion}`} />
        <CardBody className="p-0">
          {(s.cotizaciones ?? []).length === 0 ? (
            <div className="p-6 text-center text-[12px] text-ink-mute">Sin cotizaciones registradas.</div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3 text-center">Rank</th>
                  <th className="px-5 py-3">Proveedor</th>
                  <th className="px-5 py-3 text-right">Subtotal</th>
                  <th className="px-5 py-3 text-right">IVA</th>
                  <th className="px-5 py-3 text-right">Total</th>
                  <th className="px-5 py-3 text-right">Entrega (días)</th>
                  <th className="px-5 py-3">Condiciones</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {s.cotizaciones.map((c: any) => (
                  <tr key={c.id} className={cn(c.es_ganadora && 'bg-emerald-50/60')}>
                    <td className="num px-5 py-3 text-center font-bold">{c.rank_precio}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        {c.es_ganadora && <Trophy size={14} className="text-emerald-600" />}
                        {c.proveedor_nombre}
                      </div>
                      <div className="num text-[11px] text-ink-mute">{c.proveedor_rfc}</div>
                    </td>
                    <td className="num px-5 py-3 text-right">{fmt.money(c.subtotal)}</td>
                    <td className="num px-5 py-3 text-right">{fmt.money(c.iva)}</td>
                    <td className="num px-5 py-3 text-right font-semibold">{fmt.money(c.total)}</td>
                    <td className="num px-5 py-3 text-right">{c.tiempo_entrega_dias ?? '—'}</td>
                    <td className="px-5 py-3 text-[11px] text-ink-mute">{c.condiciones_pago ?? '—'}</td>
                    <td className="px-5 py-3 text-right">
                      {s.estado === 'abierta' && !c.es_ganadora && (
                        <Button size="sm" variant="secondary" onClick={() => adjudicar(c.id)}>Adjudicar</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <RegistrarCotizacionModal solID={id} open={openCot}
        onClose={() => setOpenCot(false)}
        onDone={() => { setOpenCot(false); qc.invalidateQueries({ queryKey: ['cot-solicitud', id] }); }} />
    </div>
  );
}

type Linea = { descripcion: string; cantidad: number; precio_unitario: number; marca: string; modelo: string };

function RegistrarCotizacionModal({ solID, open, onClose, onDone }: {
  solID: string; open: boolean; onClose: () => void; onDone: () => void;
}) {
  const [provID, setProvID] = useState('');
  const [tiempoEntrega, setTiempoEntrega] = useState(0);
  const [condPago, setCondPago] = useState('');
  const [iva, setIva] = useState(0);
  const [lineas, setLineas] = useState<Linea[]>([{ descripcion: '', cantidad: 1, precio_unitario: 0, marca: '', modelo: '' }]);
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const { data: provs } = useQuery({
    queryKey: ['proveedores-pick'], enabled: open,
    queryFn: () => api<{ items: Proveedor[] }>('/proveedores?limit=500'),
  });

  const subtotal = lineas.reduce((a, l) => a + l.cantidad * l.precio_unitario, 0);
  const total = subtotal + iva;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api(`/cotizaciones/solicitudes/${solID}/cotizaciones`, {
        method: 'POST',
        body: {
          proveedor_id: provID,
          tiempo_entrega_dias: tiempoEntrega,
          condiciones_pago: condPago || undefined,
          iva,
          lineas: lineas.filter(l => l.descripcion),
        },
      });
      onDone();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Registrar cotización" size="xl"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="cot-form" loading={subm}>Guardar · {fmt.money(total)}</Button>
      </>}>
      <form id="cot-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-[13px] font-medium">Proveedor</label>
            <select required value={provID} onChange={(e) => setProvID(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
              <option value="">(selecciona)</option>
              {(provs?.items ?? []).map(p => <option key={p.id} value={p.id}>{p.rfc} — {p.razon_social}</option>)}
            </select>
          </div>
          <Input name="tiempo" label="Tiempo entrega (días)" type="number"
            value={tiempoEntrega} onChange={(e) => setTiempoEntrega(parseInt(e.target.value) || 0)} />
          <Input name="cond" label="Condiciones pago" value={condPago} onChange={(e) => setCondPago(e.target.value)} />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Líneas</h4>
            <Button type="button" size="sm" variant="ghost"
              onClick={() => setLineas([...lineas, { descripcion: '', cantidad: 1, precio_unitario: 0, marca: '', modelo: '' }])}>
              + línea
            </Button>
          </div>
          <div className="mt-2 overflow-hidden rounded-md border border-border">
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[10px] uppercase text-ink-mute">
                <tr>
                  <th className="px-3 py-2">Descripción</th>
                  <th className="px-3 py-2 text-right">Cant</th>
                  <th className="px-3 py-2 text-right">P.U.</th>
                  <th className="px-3 py-2">Marca/Modelo</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lineas.map((l, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">
                      <input type="text" value={l.descripcion}
                        onChange={(e) => setLineas(lineas.map((x, j) => j === i ? { ...x, descripcion: e.target.value } : x))}
                        className="w-full h-8 rounded border border-border bg-surface px-2" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.001" value={l.cantidad}
                        onChange={(e) => setLineas(lineas.map((x, j) => j === i ? { ...x, cantidad: parseFloat(e.target.value) || 0 } : x))}
                        className="num h-8 w-20 rounded border border-border bg-surface px-2 text-right" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.0001" value={l.precio_unitario}
                        onChange={(e) => setLineas(lineas.map((x, j) => j === i ? { ...x, precio_unitario: parseFloat(e.target.value) || 0 } : x))}
                        className="num h-8 w-28 rounded border border-border bg-surface px-2 text-right" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="text" placeholder="marca / modelo"
                        value={`${l.marca}${l.modelo ? ' · ' + l.modelo : ''}`}
                        onChange={(e) => {
                          const parts = e.target.value.split(' · ');
                          setLineas(lineas.map((x, j) => j === i ? { ...x, marca: parts[0] ?? '', modelo: parts[1] ?? '' } : x));
                        }}
                        className="w-full h-8 rounded border border-border bg-surface px-2 text-[11px]" />
                    </td>
                    <td className="num px-3 py-2 text-right font-semibold">{fmt.money(l.cantidad * l.precio_unitario)}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" onClick={() => setLineas(lineas.filter((_, j) => j !== i))}
                        className="rounded p-1 text-ink-mute hover:text-danger"><Trash2 size={12} /></button>
                    </td>
                  </tr>
                ))}
                <tr className="bg-muted/40">
                  <td colSpan={4} className="px-3 py-2 text-right text-[11px] uppercase text-ink-mute">Subtotal</td>
                  <td className="num px-3 py-2 text-right font-semibold">{fmt.money(subtotal)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <Input name="iva" label="IVA" type="number" step="0.01" value={iva} onChange={(e) => setIva(parseFloat(e.target.value) || 0)} />
      </form>
    </Modal>
  );
}
